// Copyright (c) 2020 WiseTime. All rights reserved.

import { flatten, uniq } from "ramda"
import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import { SiteId } from "../user-context/slice"
import { TimeGroup, TimeRow, TrashStatus } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { AddToTimeGroupRequest } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { sortTagsListAlphabetically } from "./sorting"

export const getMatchedTimeRowsForGroup = (
  timeRows: TimeRow.AsObject[],
  timeGroup: TimeGroup.AsObject,
  currentSiteId: SiteId | undefined,
): TimeRow.AsObject[] => {
  const groupTagNames = timeGroup.tagsList
    .filter(t => t.siteId === currentSiteId)
    .map(groupTag => groupTag.name)

  if (groupTagNames.length === 0) {
    return []
  }

  return timeRows
    .filter(row => row.trashStatus !== TrashStatus.TRASHED
      && (row.timeGroupId === undefined || row.timeGroupId === "")
      && row.tagsList.filter(t => t.siteId === currentSiteId).some(rowTag => groupTagNames.includes(rowTag.name))
    )
}

export const extractUniqueTagIdsFromTimeRows = (
  timeRows: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined,
): string[] => {
  return uniq(flatten(
    timeRows.map(timeRow => timeRow.tagsList
      .filter(t => t.siteId === currentSiteId)
      .map(t => t.id))
  ))
}

export const extractUniqueTagCategoryIdsFromTimeRows = (
  timeRows: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined,
): string[] => {
  return uniq(flatten(
    timeRows.map(timeRow => timeRow.tagCategoriesList
      .filter(t => t.siteId === currentSiteId)
      .map(t => t.id))
  ))
}

export const getMatchedTagTimeRows = (
  selectedTimeRows: TimeRow.AsObject[],
  timeRowsForDate: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined,
): TimeRow.AsObject[] => {
  const tagIdsFromSelectedRows = extractUniqueTagIdsFromTimeRows(selectedTimeRows, currentSiteId)
  const tagCategoryIdsFromSelectedRows = extractUniqueTagCategoryIdsFromTimeRows(selectedTimeRows, currentSiteId)
  if (tagIdsFromSelectedRows.length === 0 && tagCategoryIdsFromSelectedRows.length === 0) {
    return []
  }

  const hasSameTagOrTagCategory = (timeRow: TimeRow.AsObject) =>
    timeRow.tagsList.filter(t => t.siteId === currentSiteId)
      .some(({ id }) => tagIdsFromSelectedRows.includes(id)) ||
    timeRow.tagCategoriesList.filter(t => t.siteId === currentSiteId)
      .some(({ id }) => tagCategoryIdsFromSelectedRows.includes(id))

  const selectedTimeRowIds = selectedTimeRows.map(r => r.id)
  return timeRowsForDate.filter(row => !selectedTimeRowIds.includes(row.id)
        && row.trashStatus !== TrashStatus.TRASHED
        && row.timeGroupId === ""
        && hasSameTagOrTagCategory(row))
}

export const hasTimeGroup = (row: TimeRow.AsObject): boolean => Boolean(row.timeGroupId)

export const hasTrashedItems = (timeRows: TimeRow.AsObject[]): boolean =>
  timeRows.some(timeRow => timeRow.trashStatus === TrashStatus.TRASHED)

export const countUnpostedUntrashedUngroupedRows = (timeRows: TimeRow.AsObject[]): number =>
  timeRows.filter(timeRow => timeRow.trashStatus !== TrashStatus.TRASHED && timeRow.timeGroupId === "").length

export const getDescription = (description: string): string => {
  if (description === "@_empty_@") {
    return "-- " + i18n._(t({ id: "msg_no_window_title_available", message: "No window title available" })) + " --"
  }
  if (description === "@_blank_@") {
    return "-- " + i18n._(t({ id: "msg_no_window_title_found", message: "No window title found" })) + " --"
  }
  return description
}

/**
 * Creates AddToTimeGroupRequest instances for adding matching time rows to multiple time groups.
 * 
 * This function implements batch processing for the magic wand functionality, allowing time rows
 * to be added to multiple time groups simultaneously based on tag matching.
 * 
 * @param timeGroups - Array of time groups to process for adding time rows
 * @param timeRows - Array of available time rows that can be added to groups
 * @param currentSiteId - Current site ID used for tag filtering (undefined means no matches possible)
 * 
 * @returns Array of AddToTimeGroupRequest instances, each containing a time group ID and list of matching time row IDs
 * 
 * Behavior:
 * - Only processes time rows that are not trashed, not already grouped, and have matching site tags
 * - Implements conflict resolution: if a time row matches multiple groups, it's assigned to the first matching group only
 * - Returns empty array if currentSiteId is undefined or no matches are found
 * - Each request contains all matching time rows for a specific time group
 */
export const buildAddToGroupRequests = (
  timeGroups: TimeGroup.AsObject[],
  timeRows: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined
): AddToTimeGroupRequest[] => {
  // If no currentSiteId, no tags will match
  if (currentSiteId === undefined) {
    return []
  }

  const requests: AddToTimeGroupRequest[] = []
  const alreadyAssignedRowIds = new Set<string>()

  timeGroups.forEach(timeGroup => {
    const allMatchedTimeRows = getMatchedTimeRowsForGroup(timeRows, timeGroup, currentSiteId)
    const availableMatchedRows = allMatchedTimeRows.filter(row => !alreadyAssignedRowIds.has(row.id))
    
    if (availableMatchedRows.length > 0) {
      availableMatchedRows.forEach(row => alreadyAssignedRowIds.add(row.id))
      
      requests.push(
        new AddToTimeGroupRequest()
          .setTimeGroupId(timeGroup.id)
          .setTimeRowIdsList(availableMatchedRows.map(r => r.id))
      )
    }
  })

  return requests
}

type TimeRowsByTags = Record<string, TimeRow.AsObject[]>
export const groupTimeRowsByTags = (timeRows: TimeRow.AsObject[], currentSiteId?: SiteId) =>
  timeRows.reduce((result: TimeRowsByTags, timeRow: TimeRow.AsObject) => {
    const key = timeRow.tagsList
      .filter(t => t.siteId === currentSiteId)
      .sort(sortTagsListAlphabetically)
      .map(t => t.name)
      .join("")
    if (result[key] === undefined) {
      result[key] = [timeRow]
    } else {
      result[key]?.push(timeRow)
    }
    return result
  }, {})
